import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Cloud, RotateCcw, CheckCircle2, AlertTriangle, Loader2, ShieldCheck, Copy } from 'lucide-react'
import clsx from 'clsx'
import { ActionRequired } from '@/components/ActionRequired'

interface Project {
  id: string
  name: string
  status: number
}

interface ProvisionResponse {
  count?: number
  needsLogin?: boolean
  message?: string
  alreadyInFlight?: number
  results: { upn: string; success: boolean; message: string | null; needsLogin: boolean; alreadyInFlight: boolean }[]
}

interface SpoSession {
  connected: boolean
  pending: boolean
  userCode: string | null
  verificationUrl: string | null
  expiresAt: string | null
  acquiredBy: string | null
  acquiredAt: string | null
  adminHost: string | null
  error: string | null
}

interface User {
  sourceUpn: string
  destinationUpn: string
  hasLicense: boolean
  isProvisioned: boolean
  error: string | null
  /// Enfileiramento aceito pelo SharePoint e ainda dentro da janela de espera: tarefa em andamento.
  inFlight: boolean
  enqueuedAt: string | null
  waitingMinutes: number | null
}

interface UsersResponse {
  projectId: string
  destinationTenantId: string
  users: User[]
}

export function OneDriveToolPage() {
  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const qc = useQueryClient()

  const projectsQ = useQuery<Project[]>({
    queryKey: ['onedrive-tool-projects'],
    queryFn: async () => await api.get<Project[]>('/api/onedrive-tool/projects'),
  })

  const usersQ = useQuery<UsersResponse>({
    queryKey: ['onedrive-tool-users', selectedProjectId],
    queryFn: async () => await api.get<UsersResponse>(`/api/onedrive-tool/projects/${selectedProjectId}/users`),
    enabled: !!selectedProjectId,
    refetchInterval: 60000, // 60s auto-refresh
  })

  // Sessão de admin do SharePoint: provisionar OneDrive é a única operação que a Microsoft
  // não aceita app-only. Um login de admin serve pra todos os provisionamentos seguintes.
  const sessionQ = useQuery<SpoSession>({
    queryKey: ['onedrive-tool-session', selectedProjectId],
    queryFn: async () => await api.get<SpoSession>(`/api/onedrive-tool/projects/${selectedProjectId}/spo-session`),
    enabled: !!selectedProjectId,
    refetchInterval: q => ((q.state.data as SpoSession | undefined)?.pending ? 3000 : 60000),
  })

  const session = sessionQ.data

  const loginMut = useMutation({
    mutationFn: async () => await api.post(`/api/onedrive-tool/projects/${selectedProjectId}/spo-session`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onedrive-tool-session', selectedProjectId] }),
  })

  const logoutMut = useMutation({
    mutationFn: async () => await api.delete(`/api/onedrive-tool/projects/${selectedProjectId}/spo-session`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['onedrive-tool-session', selectedProjectId] }),
  })

  const provisionMut = useMutation({
    mutationFn: async (upns: string[]) => {
      const body = upns.length > 0 ? { upns } : {}
      return await api.post<ProvisionResponse>(
        `/api/onedrive-tool/projects/${selectedProjectId}/provision`, body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['onedrive-tool-users', selectedProjectId] })
      qc.invalidateQueries({ queryKey: ['onedrive-tool-session', selectedProjectId] })
    },
  })

  const provisionOne = (upn: string) => provisionMut.mutate([upn])
  const provisionAll = () => provisionMut.mutate([])

  const users = usersQ.data?.users || []
  const pendingCount = users.filter(u => u.hasLicense && !u.isProvisioned && !u.inFlight).length
  const inFlightCount = users.filter(u => u.inFlight).length
  const noLicenseCount = users.filter(u => !u.hasLicense && !u.error).length
  const errorCount = users.filter(u => !!u.error).length
  const firstError = users.find(u => !!u.error)?.error ?? null
  const needsLogin = provisionMut.data?.needsLogin === true
  const canProvision = session?.connected === true
  // "Já em andamento" não é falha — é a proteção contra disparar sobre tarefa em curso.
  const provisionFailures = (provisionMut.data?.results ?? []).filter(r => !r.success && !r.alreadyInFlight)
  const provisionFailureDetail = provisionFailures[0]?.message ?? null
  const provisionEnqueued = (provisionMut.data?.results ?? []).filter(r => r.success).length
  const provisionSkipped = (provisionMut.data?.results ?? []).filter(r => r.alreadyInFlight).length

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center gap-3 mb-6">
        <Cloud className="h-7 w-7 text-brand-500" />
        <h1 className="text-2xl font-bold">OneDrive Tool</h1>
      </div>

      <p className="text-sm text-slate-600 mb-4">
        Ferramenta de provisionamento de OneDrive para usuários do tenant destino.
        Necessária porque o Microsoft Graph não provisiona OneDrive automaticamente em contas novas.
      </p>

      <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
        <label className="block text-sm font-medium mb-2">Projeto</label>
        <select
          value={selectedProjectId}
          onChange={e => setSelectedProjectId(e.target.value)}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
        >
          <option value="">Selecione um projeto</option>
          {projectsQ.data?.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {selectedProjectId && (
        <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className={clsx('h-5 w-5 mt-0.5', session?.connected ? 'text-green-600' : 'text-slate-400')} />
              <div>
                <h2 className="font-semibold text-sm">Admin do SharePoint (destino)</h2>
                {session?.connected ? (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Conectado como <span className="font-medium">{session.acquiredBy || 'admin'}</span>
                    {session.adminHost && <> • {session.adminHost}</>}
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Não conectado — o provisionamento fica bloqueado até um admin entrar.
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {session?.connected ? (
                <button
                  onClick={() => logoutMut.mutate()}
                  disabled={logoutMut.isPending}
                  className="px-3 py-1.5 text-xs font-medium rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                >
                  Desconectar
                </button>
              ) : (
                <button
                  onClick={() => loginMut.mutate()}
                  disabled={loginMut.isPending || session?.pending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
                >
                  {loginMut.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                  Conectar admin
                </button>
              )}
            </div>
          </div>

          {/* Login em andamento: o código na tela + o link, e o "Já autentiquei" que confere de verdade. */}
          {session?.pending && session.userCode && (
            <div className="mt-4">
              <ActionRequired
                tone="info"
                title="Falta autenticar o administrador"
                steps={[
                  <>Abra <strong>{session.verificationUrl || 'microsoft.com/devicelogin'}</strong> no navegador.</>,
                  <>Digite o código abaixo.</>,
                  <>Entre com uma conta que seja <strong>Administrador do SharePoint do tenant destino</strong>.</>,
                ]}
                link={{ href: session.verificationUrl || 'https://microsoft.com/devicelogin', label: 'Abrir login da Microsoft' }}
                onValidate={() => sessionQ.refetch()}
                validating={sessionQ.isFetching}
                validateLabel="Já autentiquei — validar"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <code className="px-3 py-1.5 bg-white border border-blue-300 rounded font-mono text-base tracking-widest">
                    {session.userCode}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(session.userCode || '')}
                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                  >
                    <Copy className="h-3 w-3" />
                    Copiar código
                  </button>
                  <span className="inline-flex items-center gap-1 text-xs text-blue-700">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Aguardando a autenticação...
                  </span>
                </div>
              </ActionRequired>
            </div>
          )}

          {/* Sem sessão e sem login em andamento: diz o que fazer antes de o botão fazer sentido. */}
          {session && !session.connected && !session.pending && (
            <div className="mt-4">
              <ActionRequired
                title="Provisionamento bloqueado: falta o login de um admin do SharePoint"
                steps={[
                  <>A Microsoft não deixa provisionar OneDrive só com o certificado do app — exige um administrador de verdade.</>,
                  <>Clique em <strong>Conectar admin</strong> aqui em cima: aparece um código e um link.</>,
                  <>Entre com um <strong>Administrador do SharePoint do tenant destino</strong>. Vale uma vez — a sessão se renova sozinha e serve para todos os provisionamentos seguintes.</>,
                ]}
                onValidate={() => sessionQ.refetch()}
                validating={sessionQ.isFetching}
                detail={session.error}
              />
            </div>
          )}

          {session?.connected && session.error && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
              {session.error}
            </div>
          )}
        </div>
      )}

      {/* Licença é ação humana no portal — não adianta o sistema tentar. */}
      {selectedProjectId && noLicenseCount > 0 && (
        <div className="mb-4">
          <ActionRequired
            title={`${noLicenseCount} conta(s) sem licença de OneDrive/SharePoint no destino`}
            steps={[
              <>Sem a licença ativa, o OneDrive não pode ser criado — o provisionamento nem é oferecido para essas contas.</>,
              <>Atribua uma licença com o plano <strong>SharePoint Online / OneDrive</strong> a cada conta no admin center.</>,
              <>A ativação pode levar alguns minutos; depois use o "Validei" para conferir.</>,
            ]}
            link={{ href: 'https://admin.microsoft.com/#/users', label: 'Abrir usuários no admin center' }}
            onValidate={() => usersQ.refetch()}
            validating={usersQ.isFetching}
          />
        </div>
      )}

      {/* Erro consultando o destino no Graph = quase sempre consentimento faltando/revogado. */}
      {selectedProjectId && errorCount > 0 && (
        <div className="mb-4">
          <ActionRequired
            title={`Não consegui consultar ${errorCount} conta(s) no tenant destino`}
            steps={[
              <>Normalmente é o consentimento do app no tenant destino: ausente, revogado ou incompleto.</>,
              <>Abra a página <strong>Tenants</strong>, gere o link de consentimento do tenant destino e aceite com um Administrador Global.</>,
              <>Volte aqui e clique em "Validei".</>,
            ]}
            link={{ href: '/tenants', label: 'Ir para Tenants' }}
            onValidate={() => usersQ.refetch()}
            validating={usersQ.isFetching}
            detail={firstError}
          />
        </div>
      )}

      {selectedProjectId && (
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold">Usuários do destino</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {usersQ.isLoading
                  ? 'Carregando...'
                  : `${users.length} usuário(s) • ${pendingCount} pendente(s) de provisão` +
                    (inFlightCount > 0 ? ` • ${inFlightCount} em andamento` : '')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => usersQ.refetch()}
                disabled={usersQ.isFetching}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                <RotateCcw className={clsx('h-3 w-3', usersQ.isFetching && 'animate-spin')} />
                Checar
              </button>
              {pendingCount > 0 && (
                <button
                  onClick={provisionAll}
                  disabled={provisionMut.isPending || !canProvision}
                  title={canProvision ? undefined : 'Conecte um admin do SharePoint primeiro'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50"
                >
                  {provisionMut.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                  Provisionar todos ({pendingCount})
                </button>
              )}
            </div>
          </div>

          <div className="space-y-1">
            {users.map(u => (
              <div key={u.destinationUpn} className="flex items-center gap-3 p-2 bg-slate-50 rounded text-sm">
                <div className="flex-1">
                  <div className="font-medium">{u.destinationUpn}</div>
                  <div className="text-xs text-slate-500">{u.sourceUpn}</div>
                </div>

                {!u.hasLicense && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-700 font-medium">
                    <AlertTriangle className="h-3 w-3" />
                    Sem licença OneDrive
                  </span>
                )}

                {u.hasLicense && u.isProvisioned && (
                  <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium">
                    <CheckCircle2 className="h-3 w-3" />
                    OK
                  </span>
                )}

                {u.hasLicense && !u.isProvisioned && u.inFlight && (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs text-amber-700 font-medium"
                    title="Enfileirado no SharePoint. A Microsoft cria o site de forma assíncrona."
                  >
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Enfileirado{u.waitingMinutes != null && u.waitingMinutes > 0 ? ` há ${u.waitingMinutes}min` : ''}
                  </span>
                )}

                {u.hasLicense && !u.isProvisioned && !u.inFlight && (
                  <button
                    onClick={() => provisionOne(u.destinationUpn)}
                    disabled={provisionMut.isPending || !canProvision}
                    title={canProvision ? undefined : 'Conecte um admin do SharePoint primeiro'}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                  >
                    {provisionMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Provisionar'}
                  </button>
                )}

                {u.error && (
                  <span className="text-xs text-red-600 italic max-w-md truncate" title={u.error}>
                    {u.error.substring(0, 80)}
                  </span>
                )}
              </div>
            ))}
          </div>

          {provisionMut.isSuccess && !needsLogin && provisionEnqueued > 0 && (
            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
              ✓ {provisionEnqueued} conta(s) enfileirada(s). Pode levar 5-15min pra completar.
              A página atualiza automaticamente a cada 60s.
            </div>
          )}

          {/* Disparo sobre tarefa já em andamento: informativo, não erro. */}
          {provisionMut.isSuccess && provisionSkipped > 0 && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
              {provisionSkipped} conta(s) já estavam enfileiradas e não foram disparadas de novo —
              o SharePoint ainda está criando os sites.
            </div>
          )}

          {needsLogin && (
            <div className="mt-3">
              <ActionRequired
                title="A sessão de admin do SharePoint não vale mais"
                steps={[
                  <>O enfileiramento foi recusado por falta de credencial de administrador (expirou, foi revogada, ou a conta usada não é Administrador do SharePoint).</>,
                  <>Clique em <strong>Conectar admin</strong> no card acima e autentique de novo.</>,
                  <>Repita o provisionamento.</>,
                ]}
                onValidate={() => { sessionQ.refetch(); provisionMut.reset() }}
                validating={sessionQ.isFetching}
                validateLabel="Já reconectei — validar"
                detail={provisionFailureDetail}
              />
            </div>
          )}

          {/* Falhas por conta, com o motivo cru — não sumir com o erro atrás de um "✗". */}
          {provisionFailures.length > 0 && !needsLogin && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
              <p className="font-medium mb-1">{provisionFailures.length} conta(s) não foram enfileiradas:</p>
              <ul className="space-y-0.5">
                {provisionFailures.slice(0, 10).map(r => (
                  <li key={r.upn} className="font-mono break-all">{r.upn} — {r.message}</li>
                ))}
              </ul>
            </div>
          )}

          {provisionMut.isError && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
              ✗ Erro ao disparar provisionamento.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
